/**
 * Display form for a bank account number.
 *
 * The read API carries the account number as the file's `03` record sends it,
 * because the screen has to be able to tell two accounts apart and #491 stores
 * the full value. What it does not have to do is print it: an operator
 * recognises an account by its last few digits, and a full account number on a
 * shared screen is a number nobody needed to see.
 *
 * `maskNumber` in the api-server and file-server `encryption.util.ts` does the
 * same job on the server side, but it lives in two service directories that
 * `apps/web-app` does not depend on, so it cannot be imported here. It also pads
 * its mask to the original length, which states how long the number was; this
 * one is fixed width. Worth consolidating into `@steerfi/shared` when something
 * else in the web app needs to mask a number.
 */

/** How many trailing digits stay legible. */
const VISIBLE = 4;

/**
 * Masks an account number down to its last four characters.
 *
 * A number of four characters or fewer is returned unmasked rather than being
 * padded or rejected. Masking it would hide the only thing that distinguishes
 * one account from another, and no real account number is that short, so
 * throwing here would only ever break a screen over test data.
 */
export const maskAccountNumber = (accountNumber: string): string => {
  const trimmed = accountNumber.trim();
  if (trimmed.length <= VISIBLE) return trimmed;
  return `${'*'.repeat(VISIBLE)}${trimmed.slice(-VISIBLE)}`;
};
